import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import iconTopStyles from "./TabIconTopAtom.module.css";
import iconLeftStyles from "./TabIconLeftAtom.module.css";
import { useFocusHandling } from "../../../src/hooks/useFocusHandling";
import Tag, { TagType } from "../../components/tag";
import { BadgeType } from "../../components/badge/BadgeDefault";
import { BadgeNotification } from "@components";

export type TabAtomType = {
  className?: string;
  label?: string;

  /** Variant props */
  size?: "sm" | "md";
  state?: "default" | "selected" | "disabled";
  style?: "icon-top" | "icon-left";
  tag?: TagType;
  badge?: BadgeType;
  icon?: ReactNode;
  labelIcon?: ReactNode;
  selected?: boolean;
  type?: "horizontal" | "vertical";
  onClick?: () => void;
};

const TabAtomStyles = {
  "icon-top": iconTopStyles,
  "icon-left": iconLeftStyles,
} as const;

const TabAtom: FunctionComponent<TabAtomType> = ({
  className = "",
  size = "sm",
  state: externalState = "default",
  style = "icon-top",
  label = "Label",
  icon,
  labelIcon,
  tag,
  badge,
  selected: externalSelected,
  type = "horizontal",
  onClick,
}) => {
  const [highLight, setHighLight] = useState(false);
  const [state, setState] = useState(externalState);
  const [selected, setSelected] = useState(externalSelected);
  const styles = TabAtomStyles[style];
  const TabAtomId = `quill-ui-next-tab-atom-${size}-${style}-${state}-selected-${selected}`;

  const { handleBlur, handleFocus } = useFocusHandling({
    isDisabled: state === "disabled",
    setHighlight: setHighLight,
  });

  const renderItems = () => (
    <>
      <div className={styles.label}>{label}</div>
      {labelIcon && (
        <div className={`${styles.iconSm} quill-tab-icon`}>{labelIcon}</div>
      )}
      {tag && <Tag {...tag} />}
      {badge && <BadgeNotification {...badge} />}
    </>
  );

  const handleClick = () => {
    onClick?.();
  };

  useEffect(() => {
    setSelected(externalSelected);
  }, [externalSelected]);

  useEffect(() => {
    setState(selected ? "selected" : "default");
  }, [selected]);

  useEffect(() => {
    setState(externalState);
  }, [externalState]);

  return (
    <div
      className={[
        styles.root,
        className,
        "quill-ui-next-atom",
        "quill-ui-next-no-select",
        state !== "disabled" && "quill-ui-next-pointer",
      ].join(" ")}
      data-size={size}
      data-state={state}
      data-type={type}
      data-id={TabAtomId}
      tabIndex={0}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={handleClick}
    >
      {icon && (
        <div className={`${styles.customIcon} quill-tab-icon`}>{icon}</div>
      )}

      {style === "icon-top" ? (
        <div className={styles.labelStatus}>{renderItems()}</div>
      ) : (
        renderItems()
      )}

      {highLight && <div className={styles.bgFocus} />}
    </div>
  );
};

export default TabAtom;
